/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * questions and answers for payment related helps
 */
import React from 'react';
import _ from 'lodash';
import { Collapse, Row, Col, Card, Result } from 'antd';

import { FAQ_CATEGORIES } from 'configs/index';
import { getAllQueries } from './schema';
import TextHighlighter from 'components/widgets/TextHighlighter';
import { VideoWidget } from 'components/codeExplorer/CodeWidgets';
import { POSTER_URL } from 'app-constants/codeExplorer';

interface Item {
	title: string;
	id: string;
	answers: Faq.ISchema[];
	videos: Faq.ISchema[];
}

interface IProps {
	schema: Item[];
	searchText?: string;
	collapseKey?: number;
	searchedQuestion?: string;
}

const getQuestionKey = (key: string): string => {
	return key ? `user-help-question-${key}` : '';
};

const prepareAllFaq = ({
	supportEmail,
	serviceTypes,
	faqOrder,
}: {
	supportEmail: string;
	serviceTypes: EligibilityRequestForm.IServiceType[];
	faqOrder?: string[];
}): Item[] => {
	const sortedServiceTypes = _.sortBy(serviceTypes, (service) => {
		const codeAsNumber = parseFloat(service.code);
		return isNaN(codeAsNumber) ? service.code : codeAsNumber;
	});

	const allQueries = getAllQueries(supportEmail, sortedServiceTypes);

	const prepared = _.map(FAQ_CATEGORIES, ({ category, title }) => {
		const categoryData = _.filter(allQueries, ({ category: questionCategory }) => _.includes(questionCategory, category));
		if (!_.isEmpty(categoryData)) {
			return {
				id: category,
				title,
				answers: _.filter(categoryData, (item) => !!item.answer),
				videos: _.filter(categoryData, (item) => !!item.video),
			};
		}
	});

	// sort by page FAQ category
	return _.sortBy(_.compact(prepared), (item) => (_.includes(faqOrder, item?.id) ? _.indexOf(faqOrder, item?.id) : 1000));
};

/**
 * FAQList
 * @returns FAQList
 */
const FAQList: React.FC<IProps> = ({ schema, searchText, collapseKey, searchedQuestion }) => {
	if (_.isEmpty(schema)) {
		return (
			<Card size='small' className='box-shadow-6'>
				<Result status='warning' title='No result matched with your searched phrase' />
			</Card>
		);
	}

	return (
		<Row gutter={[0, 32]}>
			{_.map(schema, (schemaItem, index) => {
				return (
					<Col span={24} key={index} id={schemaItem?.id} className='m-0 p-0'>
						<p className='font-base mb-2 font-semibold'>{schemaItem?.title}</p>
						<Row gutter={[0, 0]}>
							{/* {!_.isEmpty(schemaItem.videos) && (
								<Col span={24}>
									<List
										bordered
										size='small'
										header={<p style={{ fontWeight: '500' }}>Video(s):</p>}
										dataSource={schemaItem.videos}
										renderItem={(item) => {
											const questionKey = getQuestionKey(item.key);
											return (
												<List.Item id={questionKey} key={questionKey}>
													<Link to={item?.video || '/'} target='_blank' rel='noopener noreferrer'>
														{item.question}
													</Link>
												</List.Item>
											);
										}}
									/>
								</Col>
							)} */}
							{!_.isEmpty(schemaItem?.answers) && (
								<Col span={24}>
									<Collapse
										className='faq-collapse'
										key={collapseKey}
										defaultActiveKey={[searchedQuestion || '', getQuestionKey(_.first(schemaItem?.answers)?.key)]}
										items={_.map(schemaItem?.answers, (item) => {
											const questionKey = getQuestionKey(item?.key);
											return {
												key: questionKey,
												id: questionKey,
												label: <TextHighlighter content={item?.question} match={searchText || ''} />,
												children: (
													<>
														{item?.answer}
														{item.video && (
															<VideoWidget
																poster={POSTER_URL}
																videoSrc={item?.video}
																preload='none'
																style={{ height: '40vh', width: '100%' }}
															/>
														)}
													</>
												),
											};
										})}
									/>
								</Col>
							)}
						</Row>
					</Col>
				);
			})}
		</Row>
	);
};

export { prepareAllFaq, getQuestionKey, Item };
export default FAQList;
